import React, { useEffect, useRef } from "react";
import {
  FiAlertCircle,
  FiCheck,
  FiCheckCircle,
  FiHash,
  FiLayers,
  FiLock,
  FiMessageSquare,
  FiSearch,
} from "react-icons/fi";
import { isSpecialUser } from "../../../../../utils/environment";
import "./MissionTypeStep.css";

// 미션 구동 방식 세팅 단계 (쇼핑 방문 / 쇼핑 방문(원부) 3단계)
// 1 정보 입력 → (키워드 세팅 팝업) → 2 키워드 세팅 → 3 미션 구동 방식 세팅 → 4 완료
// 이전에는 1단계 드롭다운과 2단계 키워드 박스에 분산돼 있던 설정을 이 단계로 모았다.
// 기존에 비활성(주석) 처리돼 있던 항목: <option value="리뷰">리뷰</option>, <option value="복합">복합</option>

const MISSION_TYPES = [
  {
    value: "태그",
    title: "태그찾기",
    badge: "추천",
    desc: "상품의 랜덤한 태그를 찾는 미션",
    Icon: FiHash,
  },
  {
    value: "정답",
    title: "번호찾기",
    desc: "상품의 상품번호를 찾는 미션",
    Icon: FiSearch,
  },
  {
    value: "리뷰",
    title: "리뷰찾기",
    desc: "상품의 리뷰 내용을 기반으로 하는 미션",
    Icon: FiMessageSquare,
  },
  {
    value: "복합",
    title: "커스텀",
    desc: "키워드별로 구동 방식과 수량을 나눠서 설정",
    Icon: FiLayers,
  },
];

// 복합에서의 리뷰는 리뷰 데이터가 있을 때만 선택 가능 (아래 렌더링에서 reviewAvailable로 비활성 처리)
const COMPOSITE_ACTIONS = [
  { value: "태그", label: "태그찾기" },
  { value: "정답", label: "번호찾기" },
  { value: "리뷰", label: "리뷰찾기" },
];

const DEFAULT_MISSION_TYPE = "태그";

const MissionTypeStep = ({
  formData,
  onInputChange,
  keywordRanks = [],
  onKeywordRanksChange,
  userId,
}) => {
  // 복합 구동 방식은 특정 유저(isSpecialUser)에게만 노출
  const visibleMissionTypes = MISSION_TYPES.filter(
    (t) => t.value !== "복합" || isSpecialUser(userId),
  );
  const missionType = visibleMissionTypes.some((t) => t.value === formData.productName)
    ? formData.productName
    : DEFAULT_MISSION_TYPE;
  const isComposite = missionType === "복합";
  const daily = parseInt(formData.dailyQuantity) || 0;

  // 리뷰 데이터(상품 크롤링 시 받아온 contents)가 있어야 리뷰 선택 가능
  const reviewAvailable =
    Array.isArray(formData.contents) && formData.contents.length > 0;

  // 복합 설정 대상: 선택된 키워드 (실제 keywordRanks 인덱스 유지)
  const compositeRows = keywordRanks
    .map((item, actualIndex) => ({ item, actualIndex }))
    .filter(
      ({ item }) =>
        item.selected && item.keyword && item.keyword.trim() !== "",
    );

  // 복합 구동 방식일 때 키워드별 수량 합계 계산
  const calculateKeywordQuantitySum = () => {
    if (!isComposite) return 0;

    return compositeRows.reduce((sum, { item }) => {
      const quantity = parseInt(item.quantity) || 0;
      return sum + quantity;
    }, 0);
  };

  // 키워드 수량 변경 핸들러
  const handleKeywordQuantityChange = (actualIndex, value) => {
    const numValue = value.replace(/[^0-9]/g, "");
    updateKeywordRankField(actualIndex, "quantity", numValue);
  };

  // 키워드 구동방식 변경 핸들러
  const handleKeywordActionTypeChange = (actualIndex, value) => {
    updateKeywordRankField(actualIndex, "actionType", value);
  };

  const updateKeywordRankField = (actualIndex, field, value) => {
    if (!onKeywordRanksChange) return;
    onKeywordRanksChange(
      keywordRanks.map((item, idx) =>
        idx === actualIndex ? { ...item, [field]: value } : item,
      ),
    );
  };

  // 리뷰 데이터가 없는데 리뷰가 선택된 채로 남지 않도록 기본값으로 되돌림
  useEffect(() => {
    if (!reviewAvailable && formData.productName === "리뷰") {
      onInputChange("productName", DEFAULT_MISSION_TYPE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewAvailable, formData.productName]);

  // 미지정 값이면 기본 구동 방식(태그)으로 맞춤
  useEffect(() => {
    if (formData.productName !== missionType) {
      onInputChange("productName", missionType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.productName]);

  // 복합 선택 시 키워드별 기본값 채우기
  // - 수량이 모두 비어 있으면 일일 수량을 키워드 수로 균등 분배
  // - 구동 방식이 비어 있으면 태그
  const quantityInitializedRef = useRef(false);
  const compositeSignature = compositeRows
    .map(({ actualIndex, item }) => `${actualIndex}:${item.actionType || ""}:${item.quantity || ""}`)
    .join("|");

  useEffect(() => {
    if (!isComposite) {
      quantityInitializedRef.current = false;
      return;
    }
    if (!onKeywordRanksChange || compositeRows.length === 0) {
      return;
    }

    // 균등 분배는 복합 진입 시 한 번만 (사용자가 수량을 모두 지워도 다시 채우지 않음)
    const allQuantityEmpty =
      !quantityInitializedRef.current &&
      compositeRows.every(
        ({ item }) => !item.quantity || parseInt(item.quantity) === 0,
      );
    const needActionType = compositeRows.some(({ item }) => !item.actionType);

    quantityInitializedRef.current = true;
    if (!allQuantityEmpty && !needActionType) return;

    const count = compositeRows.length;
    const base = Math.floor(daily / count);
    const remainder = daily - base * count;
    const rowOrder = new Map(
      compositeRows.map(({ actualIndex }, order) => [actualIndex, order]),
    );

    onKeywordRanksChange(
      keywordRanks.map((item, idx) => {
        if (!rowOrder.has(idx)) return item;
        const order = rowOrder.get(idx);
        const next = { ...item };
        if (!next.actionType) next.actionType = DEFAULT_MISSION_TYPE;
        if (allQuantityEmpty && daily > 0) {
          next.quantity = String(base + (order === 0 ? remainder : 0));
        }
        return next;
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComposite, compositeSignature, daily]);

  const totalQuantity = calculateKeywordQuantitySum();
  const isMatched = totalQuantity === daily;
  const diff = daily - totalQuantity;
  const progress = daily > 0 ? Math.min((totalQuantity / daily) * 100, 100) : 0;

  return (
    <div className="mts-root">
      {/* 광고 요약: 스크롤 시에도 상단에 고정 */}
      <div className="mts-summary-wrap">
        <section className="mts-summary" aria-label="광고 요약">
          <div className="mts-thumb">
            {formData.imageUrl ? (
              <img src={formData.imageUrl} alt="상품 이미지" />
            ) : null}
          </div>
          <div className="mts-summary-main">
            <span className="mts-summary-store">{formData.storeName || "-"}</span>
            <strong className="mts-summary-name">
              {formData.missionName || "-"}
            </strong>
          </div>
          <div className="mts-summary-stat">
            <span className="mts-summary-stat-label">일일 수량</span>
            <strong className="mts-summary-stat-value">
              {daily.toLocaleString()}
              <small>회</small>
            </strong>
          </div>
        </section>
      </div>

      {/* 첫 번째 줄: 미션 구동 방식 */}
      <section className="mts-section" aria-label="미션 구동 방식">
        <div className="mts-section-head">
          <h2 className="mts-section-title">미션 구동 방식</h2>
        </div>

        <div
          className={`mts-type-grid${
            visibleMissionTypes.length === 3 ? " is-three" : ""
          }`}
          role="radiogroup"
          aria-label="미션 구동 방식"
        >
          {visibleMissionTypes.map((item) => {
            const { Icon } = item;
            const disabled = item.value === "리뷰" && !reviewAvailable;
            const selected = missionType === item.value;
            return (
              <button
                key={item.value}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={disabled}
                className={`mts-type-card${selected ? " is-selected" : ""}${
                  disabled ? " is-disabled" : ""
                }`}
                onClick={() => onInputChange("productName", item.value)}
              >
                <span className="mts-type-check" aria-hidden="true">
                  {disabled ? <FiLock /> : <FiCheck />}
                </span>
                <span className="mts-type-icon" aria-hidden="true">
                  <Icon />
                </span>
                <span className="mts-type-title">
                  {item.title}
                  {item.badge && !disabled ? (
                    <span className="mts-type-badge">{item.badge}</span>
                  ) : null}
                </span>
                <span className="mts-type-desc">{item.desc}</span>
                {disabled ? (
                  <span className="mts-type-reason">리뷰 데이터 없음</span>
                ) : null}
              </button>
            );
          })}
        </div>

        {!reviewAvailable ? (
          <p className="mts-hint">
            <FiAlertCircle />
            <span>
              리뷰 데이터가 없어 &lsquo;리뷰찾기&rsquo; 방식은 선택할 수 없습니다.
              스마트스토어 상품의 리뷰 정보가 확인되는 경우에만 선택할 수 있어요.
            </span>
          </p>
        ) : null}
      </section>

      {/* 복합 구동 방식일 때 구동방식과 수량 입력 */}
      {isComposite ? (
        <section className="mts-section mts-composite" aria-label="커스텀 설정">
          <div className="mts-section-head">
            <h2 className="mts-section-title">커스텀 설정</h2>
            <p className="mts-section-sub">
              키워드별 구동 방식과 수량을 정해주세요.
              {reviewAvailable
                ? ""
                : " 리뷰 데이터가 없어 리뷰찾기는 선택할 수 없습니다."}
            </p>
          </div>

          <div className="mts-rows">
            <div className="mts-row is-head" aria-hidden="true">
              <span>유입 키워드</span>
              <span>현재 순위</span>
              <span>구동 방식</span>
              <span>수량</span>
            </div>
            {compositeRows.map(({ item, actualIndex }, index) => (
              <div className="mts-row" key={actualIndex}>
                <span className="mts-kw">
                  <em>{index + 1}</em>
                  <b>{item.keyword}</b>
                </span>
                <span>
                  <span className="mts-rank">
                    {item.rank === 999
                      ? "999+위"
                      : item.rank === 0
                        ? "-위"
                        : `${item.rank}위`}
                  </span>
                </span>
                <span
                  className="mts-seg"
                  role="radiogroup"
                  aria-label={`${item.keyword} 구동 방식`}
                >
                  {COMPOSITE_ACTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={(item.actionType || DEFAULT_MISSION_TYPE) === opt.value}
                      disabled={opt.value === "리뷰" && !reviewAvailable}
                      title={
                        opt.value === "리뷰" && !reviewAvailable
                          ? "리뷰 데이터가 없어 선택할 수 없습니다"
                          : undefined
                      }
                      className={`mts-seg-btn${
                        (item.actionType || DEFAULT_MISSION_TYPE) === opt.value
                          ? " is-active"
                          : ""
                      }`}
                      onClick={() =>
                        handleKeywordActionTypeChange(actualIndex, opt.value)
                      }
                    >
                      {opt.value === "리뷰" && !reviewAvailable ? (
                        <FiLock />
                      ) : null}
                      {opt.label}
                    </button>
                  ))}
                </span>
                <span className="mts-qty">
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="0"
                    value={item.quantity || ""}
                    aria-label={`${item.keyword} 수량`}
                    onChange={(e) =>
                      handleKeywordQuantityChange(actualIndex, e.target.value)
                    }
                  />
                  <i>회</i>
                </span>
              </div>
            ))}
          </div>

          {/* 복합 구동 방식일 때 수량 합계 표시 */}
          <div className={`mts-sum${isMatched ? " is-ok" : " is-ng"}`}>
            <div className="mts-sum-top">
              <div className="mts-sum-nums">
                <span>키워드별 수량 합계</span>
                <strong>{totalQuantity.toLocaleString()}</strong>
                <span>/ 일일 수량 {daily.toLocaleString()}회</span>
              </div>
              <div className="mts-sum-msg">
                {isMatched ? <FiCheckCircle /> : <FiAlertCircle />}
                {isMatched
                  ? "수량이 일치합니다"
                  : diff > 0
                    ? `${diff.toLocaleString()}회 부족합니다`
                    : `${Math.abs(diff).toLocaleString()}회 초과했습니다`}
              </div>
            </div>
            <div className="mts-bar" aria-hidden="true">
              <span style={{ width: `${progress}%` }} />
            </div>
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default MissionTypeStep;
